/**
 * ORGANIZATION CARD COMPONENT
 *
 * Reusable card for displaying organization information.
 */

import { Link } from 'react-router-dom';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { StatusBadge } from '@/components/status-badge';
import { MapPin, Calendar, Mail, User, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import type { Organization } from '@/types/database';

interface OrganizationCardProps {
  organization: Organization & {
    logo?: string;
    logo_url?: string;
    locations?: Array<{ id: string; name: string; description?: string }>;
    rejection_reason?: string | null;
    review_notes?: string | null;
  };
  href?: string;
  showDetails?: boolean;
  className?: string;
}

export function OrganizationCard({
  organization,
  href,
  showDetails = true,
  className,
}: OrganizationCardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const hasContactInfo = organization.primary_contact_name || organization.primary_contact_email;

  const cardContent = (
    <Card className={`transition-all duration-200 hover:shadow-lg hover:border-primary/20 ${className || ''}`}>
      {/* Header Section */}
      <div className='p-5'>
        <div className='flex items-start justify-between gap-3 mb-3'>
          {/* Logo - Square and Rounded */}
          {(organization.logo || organization.logo_url) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='w-16 h-16 rounded-lg overflow-hidden shrink-0 bg-gray-100 dark:bg-gray-800'>
                  <img
                    src={organization.logo || organization.logo_url!}
                    alt={`${organization.name} logo`}
                    width={64}
                    height={64}
                    className='w-full h-full object-cover'
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top">Organization Logo</TooltipContent>
            </Tooltip>
          )}

          <div className='flex-1 min-w-0'>
            <CardTitle className='text-xl font-bold text-gray-900 dark:text-gray-100 mb-2 truncate'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>{organization.name}</span>
                </TooltipTrigger>
                <TooltipContent side="top">Organization</TooltipContent>
              </Tooltip>
            </CardTitle>
            <div className='mb-3'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <StatusBadge status={organization.status} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">Status</TooltipContent>
              </Tooltip>
            </div>
            {/* Stats Row */}
            <div className='flex flex-wrap gap-4 text-sm'>
              {organization.locations && organization.locations.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className='flex items-center gap-1.5 text-gray-600 dark:text-gray-400'>
                      <MapPin className='w-4 h-4 shrink-0' />
                      <span className='font-medium'>{organization.locations.length}</span>
                      <span>location{organization.locations.length !== 1 ? 's' : ''}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">Locations</TooltipContent>
                </Tooltip>
              )}
              {hasContactInfo && (
                <div className='flex items-center gap-1.5 text-green-600 dark:text-green-400'>
                  <CheckCircle2 className='w-4 h-4 shrink-0' />
                  <span>Contact info</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content Section */}
      {showDetails && (
        <CardContent className='px-5 pb-5 pt-0 space-y-4'>
          {/* Description */}
          {organization.description && (
            <p className='text-sm text-gray-700 dark:text-gray-300 leading-relaxed'>
              {organization.description}
            </p>
          )}

          {/* Contact Information Badge */}
          {hasContactInfo && (
            <div className='bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 space-y-2'>
              <div className='flex items-center gap-2 font-medium text-sm text-blue-900 dark:text-blue-200'>
                <User className='w-4 h-4' />
                Primary Contact
              </div>
              {organization.primary_contact_name && (
                <div className='text-sm text-blue-800 dark:text-blue-300 ml-6'>
                  {organization.primary_contact_name}
                </div>
              )}
              {organization.primary_contact_email && (
                <div className='flex items-center gap-2 text-sm text-blue-800 dark:text-blue-300 ml-6'>
                  <Mail className='w-3.5 h-3.5 shrink-0' />
                  <a href={`mailto:${organization.primary_contact_email}`} className='hover:underline truncate'>
                    {organization.primary_contact_email}
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Footer - Submission Date */}
          <div className='border-t pt-3 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-500'>
            <Calendar className='w-3.5 h-3.5' />
            <span>Submitted {formatDate(organization.created_at)}</span>
          </div>
        </CardContent>
      )}

      {/* Rejection Reason - Always shown if present */}
      {organization.rejection_reason && (
        <CardContent className='px-5 pb-5 pt-0'>
          <div className='bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg p-3 space-y-2'>
            <div className='flex items-center gap-2 font-medium text-sm text-red-900 dark:text-red-200'>
              <AlertCircle className='w-4 h-4' />
              Rejection Reason
            </div>
            <p className='text-sm text-red-800 dark:text-red-300 ml-6 line-clamp-3'>
              {organization.rejection_reason}
            </p>
          </div>
        </CardContent>
      )}

      {/* Review Notes - Always shown if present */}
      {organization.review_notes && (
        <CardContent className='px-5 pb-5 pt-0'>
          <div className='bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-2'>
            <div className='flex items-center gap-2 font-medium text-sm text-amber-900 dark:text-amber-200'>
              <Info className='w-4 h-4' />
              Review Notes
            </div>
            <p className='text-sm text-amber-800 dark:text-amber-300 ml-6 line-clamp-3'>
              {organization.review_notes}
            </p>
          </div>
        </CardContent>
      )}
    </Card>
  );

  if (href) {
    return (
      <Link to={href} className='block hover:shadow-md transition-shadow cursor-pointer'>
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
